import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pensionFundsApi, fondationsApi } from '../../services/api';
import Table from '../../components/Table';
import { Search, Eye } from 'lucide-react';
import type { PensionFund } from '../../types';

const PensionFundList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [fondationFilter, setFondationFilter] = useState<string>('');
  const [perPage, setPerPage] = useState(15);

  const { data, isLoading } = useQuery({
    queryKey: ['pension-funds', search, page, statusFilter, fondationFilter, perPage],
    queryFn: async () => {
      const response = await pensionFundsApi.getAll({
        search,
        page,
        status: statusFilter || undefined,
        fondation: fondationFilter || undefined,
        per_page: perPage,
      });
      return response.data;
    },
  });

  // Fetch fondations for filter
  const { data: fondationsData } = useQuery({
    queryKey: ['fondations'],
    queryFn: async () => {
      const response = await fondationsApi.getAll();
      return response.data;
    },
  });

  const columns = [
    {
      header: 'Client',
      accessor: 'client_name' as keyof PensionFund,
      className: 'text-sm font-medium text-gray-900',
    },
    {
      header: 'Caisse',
      accessor: 'fund_name' as keyof PensionFund,
      className: 'text-sm text-gray-900',
    },
    {
      header: 'Fondation',
      accessor: (row: PensionFund) => (
        <span className="text-sm text-gray-900">
          {row.fondation || '-'}
        </span>
      ),
    },
    {
      header: 'Montant',
      accessor: (row: PensionFund) => (
        <span className="text-sm font-medium text-gray-900">
          CHF {parseFloat(row.amount || '0').toLocaleString('fr-CH')}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: (row: PensionFund) => {
        const statusColors: Record<string, string> = {
          'Nouveau': 'bg-gray-100 text-gray-800',
          'En cours': 'bg-blue-100 text-blue-800',
          'Rejeté': 'bg-red-100 text-red-800',
          'Annulé': 'bg-orange-100 text-orange-800',
          'Transféré': 'bg-green-100 text-green-800',
          'Terminé': 'bg-green-100 text-green-800',
        };
        const colorClass = statusColors[row.status] || 'bg-gray-100 text-gray-800';
        return (
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
            {row.status || 'Non défini'}
          </span>
        );
      },
    },
    {
      header: 'Date',
      accessor: (row: PensionFund) => (
        <span className="text-sm text-gray-500">
          {row.created_at ? new Date(row.created_at).toLocaleDateString('fr-CH') : '-'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: PensionFund) => (
        <button
          onClick={() => navigate(`/pension-funds/${row.uuid}`)}
          className="text-primary-600 hover:text-primary-900 transition"
        >
          <Eye className="h-5 w-5" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Caisses de pension</h1>
        <p className="text-gray-600 mt-1">Gestion des transferts LPP</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par client ou caisse..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Tous les statuts</option>
            <option value="Nouveau">Nouveau</option>
            <option value="En cours">En cours</option>
            <option value="Rejeté">Rejeté</option>
            <option value="Annulé">Annulé</option>
            <option value="Transféré">Transféré</option>
            <option value="Terminé">Terminé</option>
          </select>

          <select
            value={fondationFilter}
            onChange={(e) => setFondationFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Toutes les fondations</option>
            {fondationsData?.data?.map((fondation) => (
              <option key={fondation.id} value={fondation.name}>
                {fondation.name}
              </option>
            ))}
          </select>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1); // Reset to first page when changing items per page
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value={15}>15 par page</option>
            <option value={25}>25 par page</option>
            <option value={50}>50 par page</option>
            <option value={100}>100 par page</option>
          </select>
        </div>
      </div>

      <Table
        data={data?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="Aucune caisse de pension trouvée"
        pagination={
          data?.meta
            ? {
                currentPage: data.meta.current_page,
                totalPages: data.meta.last_page,
                total: data.meta.total,
                from: data.meta.from,
                to: data.meta.to,
                onPageChange: setPage,
              }
            : undefined
        }
      />
    </div>
  );
};

export default PensionFundList;
